import PropTypes from 'prop-types'
import React, { memo, useCallback, useRef, useMemo } from 'react'
import ReactDOM from 'react-dom'
import usePopper from 'common/hooks/usePopper'
import useToggleMenu from '../../../../hooks/useToggleMenu'
import Button from 'common/components/Button'
import Icon from 'common/components/Icon'
import { RowSpacer } from 'common/components/Row'
import Text from 'common/components/Text'
import GlobalNavRowEdit from './GlobalNavRowEdit/GlobalNavRowEditContainer'
import SidebarRow from './SidebarRow'
import useTooltip from 'common/hooks/useTooltip'
import { Trans } from 'react-i18next'
import { Tooltip2 as Tooltip } from '../../../common/Tooltip'
import { sidebarWidth } from '../../../../style'
import BoardOptionsMenu from '../../../BoardOptionsMenu'

export const SidebarRowLayout = memo(
  ({
    item,
    getFolders,
    root,
    selected,
    grey,
    onClick,
    dropLocation,
    edit,
    editDone,
    editing,
    anon,
    smallScreen
  }) => {
    const folders = useMemo(() => getFolders(item), [item, getFolders])

    const menuRef = useRef()
    const [menuOpen, showMenu, hideMenu] = useToggleMenu(menuRef)
    const showMenuOnly = useCallback(
      e => {
        e.stopPropagation()
        showMenu()
      },
      [showMenu]
    )

    const menuButtonRef = useRef()
    const { style: menuStyle } = usePopper(
      {
        referenceRef: menuButtonRef,
        popperRef: menuRef
      },
      [menuOpen]
    )

    const rowRef = useRef()
    const tooltipRef = useRef()
    const [showTooltip, hideTooltip, tooltipShowing, tooltipStyle] = useTooltip(
      rowRef,
      tooltipRef,
      {
        disabled: smallScreen || !root || item.type === 'folder',
        placement: 'right',
        modifiers: { offset: { offset: '0, 10' } }
      }
    )

    return editing ? (
      <GlobalNavRowEdit item={item} editDone={editDone} />
    ) : (
      <>
        <SidebarRow
          onClick={onClick}
          onDoubleClick={edit}
          onMouseDown={hideTooltip}
          onMouseEnter={showTooltip}
          onMouseLeave={hideTooltip}
          grey={grey}
          selected={selected || menuOpen}
          dropLocation={dropLocation}
          data-id={item.id}
          innerRef={rowRef}
        >
          {item.type === 'board' ? (
            <Icon icon="board" color={item.color} active={selected} />
          ) : (
            <Icon icon="folder" color={item.color} />
          )}
          <Text truncate normal={!selected}>
            {item.name}
          </Text>

          <RowSpacer />

          {!anon && !smallScreen && (
            <Button
              onClick={showMenuOnly}
              active={menuOpen}
              innerRef={menuButtonRef}
            >
              <Icon icon="ellipsis" />
            </Button>
          )}
        </SidebarRow>

        {tooltipShowing && !menuOpen && (
          <Tooltip
            innerRef={tooltipRef}
            style={{
              ...tooltipStyle,
              maxWidth: `calc(100vw - ${sidebarWidth}px`
            }}
          >
            {folders.length ? (
              folders.map((folder, i) => (
                <React.Fragment key={folder.id}>
                  {i > 0 && <Text>&mdash;</Text>}
                  <Icon icon="folder" color={folder.color} />
                  {i === folders.length - 1 && <Text>{folder.name}</Text>}
                </React.Fragment>
              ))
            ) : item.parent ? (
              <Text>
                <Trans i18nKey="sidebar.tooltips.privateFolder" values={item}>
                  <Text bold>name</Text> <Text>in private folder</Text>
                </Trans>
              </Text>
            ) : (
              <Text>
                <Trans i18nKey="sidebar.tooltips.unfiled" values={item}>
                  <Text bold>name</Text> <Text>not in folder</Text>
                </Trans>
              </Text>
            )}
          </Tooltip>
        )}

        {menuOpen &&
          ReactDOM.createPortal(
            <BoardOptionsMenu
              itemId={item.id}
              hide={hideMenu}
              innerRef={menuRef}
              style={menuStyle}
            />,
            document.body
          )}
      </>
    )
  }
)
SidebarRowLayout.propTypes = {
  board: PropTypes.object,
  edit: PropTypes.func.isRequired,
  onClick: PropTypes.func.isRequired
}

export default SidebarRowLayout
